import type { ReactNode } from 'react';
import type { Capability } from '@dpem/contracts';

import { useAuth } from '@/app/providers/AuthProvider';
import { ForbiddenPage } from '@/app/errors/ForbiddenPage';

/**
 * Gate a route on a capability the signed-in user's roles grant.
 *
 * Renders the forbidden page rather than redirecting. A redirect rewrites the URL,
 * so the user loses the link they were sent and cannot forward it to someone who
 * *does* have the role — which is the most common next thing they want to do.
 *
 * `any` exists because two surfaces are reachable by more than one route to the
 * same work: governance is for a reviewer or an administrator, and expressing that
 * as two nested guards would render two forbidden pages deep.
 *
 * This mirrors the server's decision; it does not make it. The API refuses the same
 * request whether or not this component is in the tree.
 */

export interface RequireCapabilityProps {
  children: ReactNode;
  /** All of these are required. */
  capability?: Capability;
  /** Any one of these is enough. */
  any?: readonly Capability[];
}

export function RequireCapability({ children, capability, any }: RequireCapabilityProps) {
  const { capabilities } = useAuth();

  const permitted =
    (capability === undefined || capabilities.can(capability)) &&
    (any === undefined || capabilities.canAny(...any));

  if (!permitted) return <ForbiddenPage />;

  return <>{children}</>;
}
